import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency, Language } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Building2, ArrowLeft, ArrowRight, Sparkles, ShieldCheck, FileCheck,
  CreditCard, Landmark, CheckCircle2, UserCheck, Briefcase, FileSpreadsheet,
  Globe, Search, Filter, Zap, Scale, FileCode, Hammer, ShoppingCart, Sprout, ShieldAlert
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { RegionalPaymentModal } from './RegionalPaymentModal';

interface UkraineCenterHubViewProps {
  onLaunchAgent: (target: string, preferredLang?: Language) => void;
  onBack: () => void;
  language: Language;
  currency?: Currency;
  currentUserEmail?: string;
  onSelectLanguage?: (lang: Language) => void;
  onSelectCurrency?: (cur: Currency) => void;
}

export const UKRAINE_AGENT_IDS = [
  'contract-auditor',
  'fop-tax-declarator',
  'vat-tax-declarator',
  'construction-estimate-parser-avk',
  'tender-analyst',
  'agri-land-consultant',
  'corporate-income-tax-declarator',
  'public-accountant-kekv',
  'amku-tender-appeals',
  'dasu-monitoring-defender',
  'dstu-order-constructor',
  'legal-doc-creator',
  'agri-mpz-calculator',
  'construction-war-damage-evaluator',
  'court-compliance',
  'aml-kyc-auditor',
  'cfo-financial-analyzer',
  'payroll-tax-declarator',
  'resource-property-tax-declarator',
  'excise-tax-declarator',
  'dstu-pkmu-auditor',
  'cyber-kszi-auditor',
  'legislation-tracker'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Building2 className={className} />;
  return <IconComponent className={className} />;
};

export default function UkraineCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currency = 'UAH',
  currentUserEmail,
  onSelectLanguage,
  onSelectCurrency
}: UkraineCenterHubViewProps) {
  const [activeLang, setActiveLang] = useState<Language>(language || 'uk');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentAgent, setSelectedPaymentAgent] = useState<{ id: string; name: string; price: number; singlePrice: number } | null>(null);

  const handleOpenPayment = (id: string, name: string, price: number, singlePrice: number) => {
    setSelectedPaymentAgent({ id, name, price, singlePrice });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (language) setActiveLang(language);
  }, [language]);

  const isUk = activeLang === 'uk';
  const isPl = activeLang === 'pl';
  const isEn = activeLang === 'en';
  const isDe = activeLang === 'de';
  const isFr = activeLang === 'fr';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          const pMap: Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }> = {};
          if (data.pricing && Array.isArray(data.pricing)) {
            data.pricing.forEach((p: any) => {
              pMap[p.agentId] = {
                price: p.price,
                limit: p.limit,
                durationDays: p.durationDays,
                singlePrice: p.singlePrice || Math.round(p.price * 0.2)
              };
            });
          }
          setPricing(pMap);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const ukraineAgents = agentsConfig.filter(a => UKRAINE_AGENT_IDS.includes(a.id));

  const filteredAgents = ukraineAgents.filter(agent => {
    const name = getLocalizedAgentName(agent, activeLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, activeLang).toLowerCase();
    const q = searchQuery.toLowerCase();
    const matchesSearch = name.includes(q) || desc.includes(q);

    if (selectedCategory === 'all') return matchesSearch;
    if (selectedCategory === 'legal') return matchesSearch && (agent.id.includes('contract') || agent.id.includes('legal') || agent.id.includes('court') || agent.id.includes('legislation') || agent.id.includes('dstu-order') || agent.id.includes('dstu-pkmu'));
    if (selectedCategory === 'tax') return matchesSearch && (agent.id.includes('tax') || agent.id.includes('fop') || agent.id.includes('kekv') || agent.id.includes('cfo') || agent.id.includes('accountant') || agent.id.includes('payroll') || agent.id.includes('excise'));
    if (selectedCategory === 'construction') return matchesSearch && (agent.id.includes('construction') || agent.id.includes('avk') || agent.id.includes('damage'));
    if (selectedCategory === 'procurement') return matchesSearch && (agent.id.includes('tender') || agent.id.includes('amku') || agent.id.includes('dasu'));
    if (selectedCategory === 'cadastre_agro') return matchesSearch && (agent.id.includes('agri') || agent.id.includes('mpz') || agent.id.includes('land'));
    if (selectedCategory === 'security') return matchesSearch && (agent.id.includes('aml') || agent.id.includes('cyber') || agent.id.includes('kszi'));
    return matchesSearch;
  });

  return (
    <div className="space-y-8 animate-fade-in font-mono text-slate-900 pb-16">
      {/* Header Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-amber-950 border-2 border-amber-500/40 rounded-3xl p-6 sm:p-10 shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/15 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-bold uppercase tracking-wider">
              <span>🇺🇦</span>
              <span>
                {isUk ? 'НАЦІОНАЛЬНИЙ ОПЕРАЦІЙНИЙ ЦЕНТР УКРАЇНИ' : isPl ? 'NARODOWE CENTRUM OPERACYJNE UKRAINY' : 'UKRAINE NATIONAL ENTERPRISE HUB'}
              </span>
              <span className="text-blue-300 ml-1 font-extrabold">• ДСТУ 4163 • ПКУ • PROZORRO • АВК-5 • ДЗК • ЄДР</span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight leading-tight">
              {isUk ? 'Операційний Центр Правового та Податкового Комплаєнсу України' : isPl ? 'Centrum Zgodności Prawnej i Podatkowej Ukrainy' : 'Ukraine Legal, Tax & Procurement Enterprise Suite'}
            </h1>

            <p className="text-slate-300 text-xs sm:text-sm max-w-3xl leading-relaxed">
              {isUk
                ? 'Єдиний національний комплекс прикладних інструментів для українських підприємств: автоматизація договорів за ЦКУ, 100% комплаєнс із Податковим кодексом (ПКУ), аналіз кошторисів АВК-5 та форм КБ-2в/КБ-3 за ДБН, супровід публічних закупівель Prozorro та аудит земельного банку ДЗК.'
                : isPl
                ? 'Kompleksowy zestaw narzędzi dla firm działających na rynku ukraińskim: umowy gospodarcze, audyt podatkowy (Ordynacja podatkowa Ukrainy), kosztorysy budowlane AVK-5 (KB-2v), zamówienia publiczne Prozorro oraz audyt rejestrów DZK i EDR.'
                : 'National business operating suite for Ukraine: commercial contracts (Civil & Commercial Codes), Tax Code (PKU) audit, AVK-5/KB-2v construction estimates (DBN), Prozorro tender analytics, and DZK land cadastre audits.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            <button
              onClick={onBack}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs border border-white/20 transition-all cursor-pointer shadow-lg hover:shadow-xl"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isUk ? '← На Головну' : isPl ? '← Do Menu Głównego' : '← Home'}</span>
            </button>
          </div>
        </div>

        {/* Top Highlights Grid */}
        <div className="mt-8 pt-6 border-t border-amber-500/20 grid grid-cols-2 sm:grid-cols-4 gap-3 text-white">
          <div className="bg-slate-900/80 border border-amber-500/30 rounded-2xl p-3.5 space-y-1">
            <div className="text-[10px] text-amber-400 font-extrabold uppercase tracking-wider flex items-center gap-1">
              <Scale className="w-3.5 h-3.5" />
              <span>ЦКУ / ПКУ / ДСТУ</span>
            </div>
            <div className="text-xs text-slate-300 font-bold">100% Правова сила</div>
          </div>

          <div className="bg-slate-900/80 border border-blue-500/30 rounded-2xl p-3.5 space-y-1">
            <div className="text-[10px] text-blue-400 font-extrabold uppercase tracking-wider flex items-center gap-1">
              <FileCode className="w-3.5 h-3.5" />
              <span>ПКУ & ДПС ЗВІТНІСТЬ</span>
            </div>
            <div className="text-xs text-slate-300 font-bold">ПДВ, ЄП, КЕКВ, РРО</div>
          </div>

          <div className="bg-slate-900/80 border border-amber-500/30 rounded-2xl p-3.5 space-y-1">
            <div className="text-[10px] text-amber-400 font-extrabold uppercase tracking-wider flex items-center gap-1">
              <Hammer className="w-3.5 h-3.5" />
              <span>КОШТОРИСИ АВК-5</span>
            </div>
            <div className="text-xs text-slate-300 font-bold">КБ-2в, КБ-3, ДБН А3.1</div>
          </div>

          <div className="bg-slate-900/80 border border-emerald-500/30 rounded-2xl p-3.5 space-y-1">
            <div className="text-[10px] text-emerald-400 font-extrabold uppercase tracking-wider flex items-center gap-1">
              <ShoppingCart className="w-3.5 h-3.5" />
              <span>PROZORRO & АМКУ</span>
            </div>
            <div className="text-xs text-slate-300 font-bold">Тендери та оскарження</div>
          </div>
        </div>
      </div>

      {/* Category Tabs & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-none">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
              selectedCategory === 'all'
                ? 'bg-amber-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            {isUk ? 'Усі Українські Модулі' : isPl ? 'Wszystkie Moduły UA' : 'All Ukraine Modules'} ({ukraineAgents.length})
          </button>

          <button
            onClick={() => setSelectedCategory('legal')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'legal'
                ? 'bg-amber-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <Scale className="w-3.5 h-3.5" />
            <span>{isUk ? '⚖️ Право & Договори' : isPl ? '⚖️ Prawo i Umowy' : '⚖️ Legal & Contracts'}</span>
          </button>

          <button
            onClick={() => setSelectedCategory('tax')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'tax'
                ? 'bg-amber-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <FileCode className="w-3.5 h-3.5" />
            <span>{isUk ? '💰 Податки & Бухгалтерія' : isPl ? '💰 Podatki i Księgowość' : '💰 Tax & Accounting'}</span>
          </button>

          <button
            onClick={() => setSelectedCategory('construction')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'construction'
                ? 'bg-amber-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <Hammer className="w-3.5 h-3.5" />
            <span>{isUk ? '🏗️ Будівництво & АВК-5' : isPl ? '🏗️ Budownictwo AVK-5' : '🏗️ Construction & AVK-5'}</span>
          </button>

          <button
            onClick={() => setSelectedCategory('procurement')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'procurement'
                ? 'bg-amber-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            <span>{isUk ? '🏛️ Prozorro & Тендери' : isPl ? '🏛️ Prozorro i Przetargi' : '🏛️ Prozorro & Tenders'}</span>
          </button>

          <button
            onClick={() => setSelectedCategory('cadastre_agro')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'cadastre_agro'
                ? 'bg-amber-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <Sprout className="w-3.5 h-3.5" />
            <span>{isUk ? '🌾 Земля & ДЗК' : isPl ? '🌾 Ziemia i DZK' : '🌾 Land Cadastre'}</span>
          </button>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-72 shrink-0">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isUk ? 'Пошук модуля...' : isPl ? 'Szukaj modułu...' : 'Search module...'}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-900 focus:outline-hidden focus:border-amber-600 shadow-xs"
          />
        </div>
      </div>

      {/* Agents Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent) => {
          const name = getLocalizedAgentName(agent, activeLang);
          const desc = getLocalizedAgentDesc(agent, activeLang);
          const features = getLocalizedAgentFeatures(agent, activeLang);
          const agentPricing = pricing[agent.id] || { price: 500, singlePrice: 100 };

          return (
            <div
              key={agent.id}
              className="group bg-white border-2 border-slate-200 hover:border-amber-500 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative overflow-hidden"
            >
              {/* Top Accent Gradient */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-amber-400 to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="space-y-4">
                {/* Icon & Category */}
                <div className="flex items-start justify-between gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-800 shadow-xs group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-6 h-6" />
                  </div>
                  <div className="text-right">
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-blue-50 text-blue-800 border border-blue-200">
                      🇺🇦 {isUk ? 'УКРАЇНА • ДСТУ' : 'UKRAINE • DSTU'}
                    </span>
                  </div>
                </div>

                {/* Agent Name */}
                <div>
                  <h3 className="text-base font-black text-slate-900 leading-snug group-hover:text-amber-700 transition-colors line-clamp-2">
                    {name}
                  </h3>
                  <p className="text-xs text-slate-600 mt-2 line-clamp-3 leading-relaxed">
                    {desc}
                  </p>
                </div>

                {/* Key Features List */}
                {features && features.length > 0 && (
                  <div className="pt-2 border-t border-slate-100 space-y-1.5">
                    {features.slice(0, 3).map((feat, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-[11px] text-slate-600 leading-tight">
                        <CheckCircle2 className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{feat}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom Action & Pricing Bar */}
              <div className="pt-5 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">
                    {isUk ? 'Тариф (30 зап):' : isPl ? 'Taryfa (30 zap):' : 'Plan (30 reqs):'}
                  </div>
                  <div className="text-sm font-black text-slate-900">
                    {currency === 'UAH' ? `${agentPricing.price} грн` : currency === 'PLN' ? `${Math.round(agentPricing.price / 10)} zł` : `€${Math.round(agentPricing.price / 45)}`}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenPayment(agent.id, name, agentPricing.price, agentPricing.singlePrice)}
                    className="p-2.5 rounded-xl border border-slate-200 hover:border-amber-400 bg-slate-50 hover:bg-amber-50 text-amber-800 transition-all cursor-pointer"
                    title={isUk ? 'Оплатити / Підписка' : isPl ? 'Kup dostęp' : 'Subscribe'}
                  >
                    <CreditCard className="w-4 h-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onLaunchAgent(agent.target, activeLang)}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 active:scale-95 text-white font-extrabold text-xs shadow-md hover:shadow-amber-500/30 transition-all cursor-pointer"
                  >
                    <span>{isUk ? '🚀 Відкрити' : isPl ? '🚀 Uruchom' : '🚀 Open'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Regional Payment Modal */}
      {selectedPaymentAgent && (
        <RegionalPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          agentId={selectedPaymentAgent.id}
          agentName={selectedPaymentAgent.name}
          currency={currency}
          userEmail={currentUserEmail}
          initialPrice={selectedPaymentAgent.price}
          initialSinglePrice={selectedPaymentAgent.singlePrice}
          jurisdiction="UA"
        />
      )}
    </div>
  );
}
